/**
 * PWA-UPDATE-1 — the only thing the user ever sees from the update mechanism.
 *
 * Most of the time this renders nothing: an update that arrives while the app is idle just reloads,
 * and if that reload lands within a few seconds of the app being opened it is silent, because it is
 * indistinguishable from a normal launch.
 *
 * The chip appears in one situation — the update is ready but a screen has claimed to be holding
 * unsaved work, so the reload has been deferred. It is a persistent chip rather than a toast on
 * purpose: a toast expires, and if a user stays on the bulk-import review for forty minutes they
 * would have been told once, early, and then left with no way to act on it. "Refresh now" is the user
 * consenting, so it bypasses the busy gate.
 *
 * No i18n keys: this is transient chrome, visible for seconds, and adding two locale keys to every
 * language for it is not worth the drift.
 */
import { useSyncExternalStore } from 'react';
import { subscribeUpdateState, getUpdateState, forceUpdateReload } from '../../pwa/swUpdate';

export function UpdateReadyChip() {
  const state = useSyncExternalStore(subscribeUpdateState, getUpdateState, getUpdateState);

  if (!state.chipVisible) return null;
  const reloading = state.phase === 'reloading';

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        left: '50%',
        transform: 'translateX(-50%)',
        bottom: 'calc(env(safe-area-inset-bottom, 0px) + 76px)',
        zIndex: 9998,
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        maxWidth: 'calc(100vw - 32px)',
        padding: '10px 14px',
        borderRadius: 999,
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        boxShadow: '0 6px 24px rgba(0,0,0,0.18)',
        fontSize: 13,
        color: 'var(--t1)',
      }}
    >
      <span style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
        {reloading ? 'Updating…' : 'Update ready'}
      </span>
      {!reloading && (
        <button
          type="button"
          onClick={forceUpdateReload}
          style={{
            border: 'none',
            background: 'var(--p)',
            color: '#fff',
            borderRadius: 999,
            padding: '6px 12px',
            fontSize: 12,
            fontWeight: 700,
            cursor: 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          Refresh now
        </button>
      )}
    </div>
  );
}

export default UpdateReadyChip;
